/**
 * Country Agency Landing Pages — Wave 4
 * ~20 new Agency pages:
 * - 11 brand new countries (same as BPO Wave 4)
 * - 9 gap-fill countries (BPO exists, Agency missing)
 */
import type { CountryAgencyData } from './country-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, React/Node.js, cloud-native deployment, and scaling to millions of users.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with React, TypeScript, and modern cloud infrastructure.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter. From MVP to enterprise-grade mobile solutions.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, recommendation engines, and LLM/GPT integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, Kubernetes, monitoring, and 24/7 managed services.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems. Figma-first approach with WCAG accessibility compliance.' },
];
const sharedKPIs = [
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '80%+', label: 'Test Coverage' },
  { value: '<2h', label: 'Response Time' },
  { value: 'ISO 27001', label: 'Certified' },
];
const sharedTimeline = [
  { week: 'W1', milestone: 'Discovery call, requirements analysis, and architecture design' },
  { week: 'W2', milestone: 'Team assembly, developer interviews, and environment setup' },
  { week: 'W3', milestone: 'Sprint 1 kickoff with daily standups and code reviews' },
  { week: 'W4', milestone: 'First deliverable, demo, and iteration feedback loop' },
];
const sharedRelated = [
  { label: 'Digital Studio Overview', href: '/services/digital-studio' },
  { label: 'BPO Services', href: '/services/bpo' },
  { label: 'Pricing Models', href: '/pricing' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Contact Us', href: '/contact' },
  { label: 'Industries We Serve', href: '/industries' },
];

function make(overrides: Partial<CountryAgencyData> & Pick<CountryAgencyData, 'slug' | 'countryName' | 'countryFlag' | 'timezone' | 'languages' | 'currency' | 'title' | 'description' | 'keywords' | 'heroTitle' | 'heroSubtitle' | 'whyTitle' | 'whyIntro' | 'whyReasons' | 'advantages' | 'pricingComparison' | 'industries' | 'faqs'>): CountryAgencyData {
  return {
    heroStats: [
      { value: '200+', label: 'Engineers' },
      { value: '60%', label: 'Cost Savings' },
      { value: '2 wks', label: 'Team Setup' },
      { value: '24/7', label: 'Support' },
    ],
    services: sharedServices,
    kpis: sharedKPIs,
    timelineSteps: sharedTimeline,
    relatedLinks: sharedRelated,
    ...overrides,
  };
}

export const COUNTRY_AGENCY_PAGES_WAVE4: Record<string, CountryAgencyData> = {

  // ═══════════════════════════════════════════════════════════
  // GAP-FILL: Countries with BPO pages but missing Agency
  // ═══════════════════════════════════════════════════════════

  philippines: make({
    slug: 'software-development-agency-for-philippines',
    countryName: 'Philippines',
    countryFlag: '🇵🇭',
    timezone: 'PHT (GMT+8)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'PHP / USD',
    title: 'Software Development Agency Philippines 2026 – Call IT Dev',
    description: 'Software development agency for Philippine companies. EU & MENA engineering, GDPR-compliant, multilingual. From $25/hr.',
    keywords: ['software development agency Philippines', 'IT outsourcing Philippines', 'web development Manila'],
    heroTitle: 'Software Development Agency for Philippines — EU & MENA Bridge',
    heroSubtitle: 'Philippine companies: European engineering, Arabic capability, GDPR compliance. CET timezone complement. From $25/hr.',
    whyTitle: 'Why Philippine Companies Partner with Call IT Dev',
    whyIntro: 'The Philippines\' BPO expertise is unmatched — but for European & MENA software markets, Morocco adds critical language and compliance capabilities.',
    whyReasons: [
      { icon: '🌍', title: 'EU Languages', description: 'Native French, German, Spanish — rare in Philippines.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: 'CET (GMT+1) complements PHT (GMT+8).' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for EU-grade engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'Essential for EU market software.' },
      { icon: '🗣️', title: 'Arabic', description: 'Native Arabic for MENA markets.' },
      { icon: '🏆', title: 'Quality', description: 'CI/CD, 80%+ test coverage, code reviews.' },
    ],
    advantages: [
      { title: 'EU Market Entry', description: 'Build GDPR-compliant products with European UX.' },
      { title: 'MENA Expansion', description: 'Arabic-first interfaces and RTL expertise.' },
      { title: 'Follow-the-Sun', description: '7h offset extends development hours.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, cloud — all in-house.' },
      { title: 'Cost Effective', description: 'Competitive rates for enterprise quality.' },
      { title: 'Flexible', description: 'Staff aug, dedicated teams, or fixed-price.' },
    ],
    pricingComparison: { local: '$10-25', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'BPO Tech', description: 'Software for BPO companies — workforce management, analytics.' },
      { name: 'FinTech', description: 'GDPR-compliant financial platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail for EU markets.' },
      { name: 'Gaming', description: 'Game development and platforms.' },
      { name: 'Healthcare', description: 'Telemedicine and health management.' },
      { name: 'EdTech', description: 'Learning platforms for global markets.' },
    ],
    faqs: [
      { question: 'How does Morocco complement Philippine IT?', answer: 'We add European languages (French, German, Italian), GDPR compliance, and CET timezone — enabling Philippine companies to serve EU and MENA markets with native-quality engineering.' },
    ],
  }),

  nigeria: make({
    slug: 'software-development-agency-for-nigeria',
    countryName: 'Nigeria',
    countryFlag: '🇳🇬',
    timezone: 'WAT (GMT+1)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'NGN / USD',
    title: 'Software Development Agency Nigeria 2026 – Call IT Dev',
    description: 'Software development agency for Nigerian companies. EU & MENA engineering, GDPR-compliant, multilingual. From $25/hr.',
    keywords: ['software development agency Nigeria', 'IT outsourcing Nigeria', 'web development Lagos'],
    heroTitle: 'Software Development Agency for Nigeria — Pan-African Digital Partner',
    heroSubtitle: 'Nigerian companies: European engineering, Arabic capability, GDPR-ready. Same WAT/CET timezone. From $25/hr.',
    whyTitle: 'Why Nigerian Companies Partner with Call IT Dev',
    whyIntro: 'Nigeria\'s tech ecosystem is Africa\'s largest. Morocco adds European language engineering and GDPR compliance for international expansion.',
    whyReasons: [
      { icon: '🌍', title: 'EU Languages', description: 'Native French, German, Spanish for European markets.' },
      { icon: '⏰', title: 'Same Timezone', description: 'WAT (GMT+1) = CET. Real-time collaboration.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'Critical for EU market entry.' },
      { icon: '🗣️', title: 'Arabic', description: 'Native Arabic for MENA expansion.' },
      { icon: '🤝', title: 'African Solidarity', description: 'Pan-African partnership for continental growth.' },
    ],
    advantages: [
      { title: 'EU Compliance', description: 'GDPR-ready software for European markets.' },
      { title: 'Same Timezone', description: 'Zero offset — seamless daily collaboration.' },
      { title: 'Francophone Africa', description: 'Cover Francophone West African markets.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, mobile, AI.' },
      { title: 'MENA Bridge', description: 'Arabic for Middle Eastern expansion.' },
      { title: 'Scale', description: 'From 2 to 50+ engineers.' },
    ],
    pricingComparison: { local: '$10-25', morocco: '$25-40', savings: 'Quality premium' },
    industries: [
      { name: 'FinTech', description: 'Platforms for Nigeria\'s booming fintech sector.' },
      { name: 'E-commerce', description: 'Cross-border retail platforms.' },
      { name: 'AgriTech', description: 'Digital agriculture platforms.' },
      { name: 'EdTech', description: 'Learning management systems.' },
      { name: 'LogTech', description: 'Logistics and delivery platforms.' },
      { name: 'HealthTech', description: 'Telemedicine and health solutions.' },
    ],
    faqs: [
      { question: 'How does Morocco compare to Nigerian developers?', answer: 'We complement Nigerian talent by adding European languages, GDPR expertise, and CET timezone alignment for EU clients. Same timezone, zero communication friction.' },
    ],
  }),

  vietnam: make({
    slug: 'software-development-agency-for-vietnam',
    countryName: 'Vietnam',
    countryFlag: '🇻🇳',
    timezone: 'ICT (GMT+7)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'VND / USD',
    title: 'Software Development Agency Vietnam 2026 – Call IT Dev',
    description: 'Software development agency for Vietnamese companies. EU & MENA engineering, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Vietnam', 'IT outsourcing Vietnam', 'web development Ho Chi Minh'],
    heroTitle: 'Software Development Agency for Vietnam — EU Market Bridge',
    heroSubtitle: 'Vietnamese companies going global: European languages, GDPR compliance, CET timezone. Follow-the-sun development.',
    whyTitle: 'Why Vietnamese Companies Partner with Call IT Dev',
    whyIntro: 'Vietnam\'s tech sector expanding to Europe needs GDPR-compliant, multilingual engineering in the CET timezone.',
    whyReasons: [
      { icon: '🌍', title: 'EU Languages', description: 'Native French, German, Spanish for European markets.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: '6h offset for extended development coverage.' },
      { icon: '💰', title: 'EU Premium', description: 'European-quality engineering at $25-40/hr.' },
      { icon: '🛡️', title: 'GDPR', description: 'Essential for EU market entry.' },
      { icon: '🗣️', title: 'French Heritage', description: 'Shared Francophone connection with Vietnam.' },
      { icon: '🏆', title: 'Enterprise Quality', description: 'ISO 27001, CI/CD, 80%+ test coverage.' },
    ],
    advantages: [
      { title: 'EU Expansion', description: 'Build EU-market-ready products with GDPR compliance.' },
      { title: 'Francophone Bond', description: 'Shared French-language heritage enables deeper collaboration.' },
      { title: 'Follow-the-Sun', description: '6h timezone complement extends productive hours.' },
      { title: 'Full-Stack', description: 'All modern technologies in-house.' },
      { title: 'Cost Effective', description: 'Competitive for EU-grade engineering.' },
      { title: 'Flexible', description: 'Staff aug, dedicated teams, or project-based.' },
    ],
    pricingComparison: { local: '$12-30', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'E-commerce', description: 'Cross-border retail for European markets.' },
      { name: 'FinTech', description: 'GDPR-compliant financial platforms.' },
      { name: 'Gaming', description: 'Game development and platforms.' },
      { name: 'SaaS', description: 'Product development and scaling.' },
      { name: 'Manufacturing', description: 'Enterprise platforms and IoT.' },
      { name: 'AgriTech', description: 'Digital agriculture solutions.' },
    ],
    faqs: [
      { question: 'What shared heritage do Morocco and Vietnam have?', answer: 'Both countries share French-language heritage, creating natural synergies in communication and project management. Our teams collaborate seamlessly with Vietnamese companies familiar with Francophone business culture.' },
    ],
  }),

  kenya: make({
    slug: 'software-development-agency-for-kenya',
    countryName: 'Kenya',
    countryFlag: '🇰🇪',
    timezone: 'EAT (GMT+3)',
    languages: ['English', 'French', 'Arabic', 'Swahili'],
    currency: 'KES / USD',
    title: 'Software Development Agency Kenya 2026 – Call IT Dev',
    description: 'Software development agency for Kenyan companies. EU & MENA engineering, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Kenya', 'IT outsourcing Kenya', 'web development Nairobi'],
    heroTitle: 'Software Development Agency for Kenya — Pan-African Tech Partner',
    heroSubtitle: 'Kenyan companies: European engineering + Arabic capability. 2h timezone offset, GDPR-compliant. From $25/hr.',
    whyTitle: 'Why Kenyan Companies Partner with Call IT Dev',
    whyIntro: 'Kenya\'s Silicon Savannah needs EU-compliant engineering for international expansion. Morocco delivers with close timezone and multilingual teams.',
    whyReasons: [
      { icon: '🌍', title: 'EU Languages', description: 'Native French, German, Spanish for European markets.' },
      { icon: '⏰', title: '2h Offset', description: 'GMT+1 vs GMT+3 — strong daily overlap.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU-grade data protection compliance.' },
      { icon: '🗣️', title: 'Arabic', description: 'Native Arabic for MENA market products.' },
      { icon: '🤝', title: 'Africa Partnership', description: 'Pan-African collaboration for continental products.' },
    ],
    advantages: [
      { title: 'EU Gateway', description: 'Build GDPR-compliant products for European expansion.' },
      { title: 'Close Timezone', description: '2h offset for strong daily collaboration.' },
      { title: 'Francophone Coverage', description: 'Serve Francophone Africa with native speakers.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, mobile, AI, cloud.' },
      { title: 'M-Pesa Expertise', description: 'Mobile money and fintech development.' },
      { title: 'Scale', description: 'Start small, grow to 50+ engineers.' },
    ],
    pricingComparison: { local: '$15-35', morocco: '$25-40', savings: 'EU quality' },
    industries: [
      { name: 'FinTech & M-Pesa', description: 'Mobile money and payment platforms.' },
      { name: 'AgriTech', description: 'Digital agriculture and supply chain.' },
      { name: 'HealthTech', description: 'Telemedicine and health platforms.' },
      { name: 'EdTech', description: 'Learning management systems.' },
      { name: 'LogTech', description: 'Last-mile delivery platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail platforms.' },
    ],
    faqs: [
      { question: 'Can you build M-Pesa-integrated platforms?', answer: 'Yes. Our teams have experience with mobile money integrations including M-Pesa, and can build platforms that combine European payment methods with African mobile money systems.' },
    ],
  }),

  argentina: make({
    slug: 'software-development-agency-for-argentina',
    countryName: 'Argentina',
    countryFlag: '🇦🇷',
    timezone: 'ART (GMT-3)',
    languages: ['Spanish (native)', 'English', 'French', 'Portuguese'],
    currency: 'ARS / USD',
    title: 'Software Development Agency Argentina 2026 – Call IT Dev',
    description: 'Software development agency for Argentine companies. EU engineering, GDPR-compliant, multilingual. From $25/hr.',
    keywords: ['software development agency Argentina', 'IT outsourcing Argentina', 'desarrollo software Buenos Aires'],
    heroTitle: 'Software Development Agency for Argentina — EU Digital Partner',
    heroSubtitle: 'Argentine companies: European engineering with native Spanish. EU expansion, GDPR compliance. From $25/hr.',
    whyTitle: 'Why Argentine Companies Partner with Call IT Dev',
    whyIntro: 'Argentina\'s world-class developers expanding to Europe need GDPR compliance, EU languages, and CET timezone engineering.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish', description: 'Seamless communication with Argentine teams.' },
      { icon: '🌍', title: 'EU Languages', description: 'French, German, Italian for European markets.' },
      { icon: '💰', title: 'Cost Effective', description: '$25-40/hr for EU-grade engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU data protection compliance.' },
      { icon: '⏰', title: 'Extended Hours', description: '4h offset extends your productive day.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, CI/CD, rigorous code reviews.' },
    ],
    advantages: [
      { title: 'EU Expansion', description: 'GDPR-ready products for European markets.' },
      { title: 'Spanish Bridge', description: 'Native Spanish + European languages from one partner.' },
      { title: 'Extended Coverage', description: '4h timezone complement.' },
      { title: 'Full-Stack', description: 'All modern technologies.' },
      { title: 'FinTech', description: 'Experience with Argentine and EU fintech regulations.' },
      { title: 'Flexible', description: 'Any engagement model.' },
    ],
    pricingComparison: { local: '$20-45', morocco: '$25-40', savings: 'EU quality' },
    industries: [
      { name: 'FinTech', description: 'Payment and banking platforms.' },
      { name: 'AgriTech', description: 'Digital agriculture for Argentina\'s agro sector.' },
      { name: 'E-commerce', description: 'Cross-border retail.' },
      { name: 'SaaS', description: 'Product development and scaling.' },
      { name: 'Gaming', description: 'Game development platforms.' },
      { name: 'EdTech', description: 'Learning platforms.' },
    ],
    faqs: [
      { question: 'How does Morocco complement Argentine developers?', answer: 'We add European languages (French, German, Italian), GDPR compliance, and CET timezone coverage — enabling Argentine companies to serve EU clients with native-quality engineering and same-day collaboration.' },
    ],
  }),

  ukraine: make({
    slug: 'software-development-agency-for-ukraine',
    countryName: 'Ukraine',
    countryFlag: '🇺🇦',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'UAH / USD / EUR',
    title: 'Software Development Agency Ukraine 2026 – Call IT Dev',
    description: 'Software development agency for Ukrainian companies. EU languages, GDPR-compliant, stable infrastructure. From $25/hr.',
    keywords: ['software development agency Ukraine', 'IT outsourcing Ukraine', 'web development Kyiv'],
    heroTitle: 'Software Development Agency for Ukraine — Stable EU Engineering Partner',
    heroSubtitle: 'Ukrainian companies: stable infrastructure, European languages, GDPR compliance. 1h timezone offset. From $25/hr.',
    whyTitle: 'Why Ukrainian Companies Partner with Call IT Dev',
    whyIntro: 'Ukraine\'s world-class tech talent can be complemented with Morocco\'s stable infrastructure, EU language capabilities, and business continuity.',
    whyReasons: [
      { icon: '🛡️', title: 'Stable Operations', description: 'Redundant infrastructure, business continuity guaranteed.' },
      { icon: '🗣️', title: 'EU Languages', description: 'French, German, Arabic — expand your language capabilities.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical timezone for real-time collaboration.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise engineering.' },
      { icon: '🌍', title: 'MENA Bridge', description: 'Native Arabic for Middle Eastern markets.' },
      { icon: '📋', title: 'GDPR', description: 'EU-grade compliance and certifications.' },
    ],
    advantages: [
      { title: 'Business Continuity', description: 'Stable, redundant operations complement Ukrainian teams.' },
      { title: 'Language Expansion', description: 'French, Arabic, German — unavailable at scale in Ukraine.' },
      { title: 'Close Timezone', description: '1h difference for seamless collaboration.' },
      { title: 'EU Compliance', description: 'GDPR, ISO 27001 certifications.' },
      { title: 'Full-Stack', description: 'All modern technologies in-house.' },
      { title: 'Flexible Models', description: 'Any engagement model available.' },
    ],
    pricingComparison: { local: '$25-50', morocco: '$25-40', savings: 'Stability premium' },
    industries: [
      { name: 'SaaS & Tech', description: 'Product engineering for Ukrainian tech companies.' },
      { name: 'FinTech', description: 'GDPR-compliant financial platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail platforms.' },
      { name: 'Gaming', description: 'Game development and platforms.' },
      { name: 'Defense & Security', description: 'Secure software development.' },
      { name: 'Agriculture', description: 'AgriTech platforms.' },
    ],
    faqs: [
      { question: 'Can you provide business continuity for Ukrainian teams?', answer: 'Yes. Our stable Casablanca operations provide redundancy and continuity for Ukrainian companies. We maintain uninterrupted service with tier-3 infrastructure and multiple connectivity backups.' },
    ],
  }),

  egypt: make({
    slug: 'software-development-agency-for-egypt',
    countryName: 'Egypt',
    countryFlag: '🇪🇬',
    timezone: 'EET (GMT+2)',
    languages: ['Arabic (native)', 'English', 'French', 'German'],
    currency: 'EGP / USD',
    title: 'Software Development Agency Egypt 2026 – Call IT Dev',
    description: 'Software development agency for Egyptian companies. Arabic-native engineering, EU expansion, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Egypt', 'IT outsourcing Egypt', 'web development Cairo'],
    heroTitle: 'Software Development Agency for Egypt — MENA & EU Engineering Partner',
    heroSubtitle: 'Egyptian companies: native Arabic engineering + European language capability. 1h timezone offset, GDPR-compliant. From $25/hr.',
    whyTitle: 'Why Egyptian Companies Partner with Call IT Dev',
    whyIntro: 'Egypt\'s tech sector expanding internationally needs European language engineering and GDPR compliance. Morocco offers Arabic-native teams plus EU capabilities.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Arabic', description: 'Arabic-first engineering with RTL expertise.' },
      { icon: '🌍', title: 'EU Languages', description: 'French, German, Spanish for European expansion.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical timezone for collaboration.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise quality.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU-grade data protection.' },
      { icon: '🤝', title: 'MENA Affinity', description: 'Deep cultural and linguistic understanding.' },
    ],
    advantages: [
      { title: 'Arabic-First', description: 'Native Arabic engineering for MENA products.' },
      { title: 'EU Gateway', description: 'European language UX for market expansion.' },
      { title: 'Close Timezone', description: '1h difference — seamless daily collaboration.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, mobile, AI.' },
      { title: 'RTL Expertise', description: 'Arabic/Hebrew RTL interfaces built natively.' },
      { title: 'Compliance', description: 'ISO 27001, GDPR.' },
    ],
    pricingComparison: { local: '$10-25', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'FinTech', description: 'Islamic fintech and digital banking platforms.' },
      { name: 'E-commerce', description: 'Arabic-first retail platforms.' },
      { name: 'EdTech', description: 'Arabic learning platforms.' },
      { name: 'Real Estate', description: 'Property management platforms.' },
      { name: 'Tourism', description: 'Travel and booking platforms.' },
      { name: 'HealthTech', description: 'Telemedicine solutions.' },
    ],
    faqs: [
      { question: 'Can you build Arabic-first applications?', answer: 'Absolutely. Our native Arabic engineers specialize in RTL interfaces, Arabic NLP, and culturally-aware UX. We also add European languages for international market expansion.' },
    ],
  }),

  greece: make({
    slug: 'software-development-agency-for-greece',
    countryName: 'Greece',
    countryFlag: '🇬🇷',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'EUR',
    title: 'Software Development Agency Greece 2026 – Call IT Dev',
    description: 'Software development agency for Greek companies. EU nearshore, multilingual, GDPR-compliant. 50% savings vs Athens. From €25/hr.',
    keywords: ['software development agency Greece', 'IT outsourcing Greece', 'web development Athens'],
    heroTitle: 'Software Development Agency for Greece — EU Nearshore Partner',
    heroSubtitle: 'Greek companies: EU-grade engineering at 50% savings vs Athens. French, Arabic, German capabilities. 1h timezone offset.',
    whyTitle: 'Why Greek Companies Choose Call IT Dev',
    whyIntro: 'Greece\'s growing tech scene needs cost-effective, multilingual engineering. Morocco delivers EU-grade quality at 50% savings with 1h timezone difference.',
    whyReasons: [
      { icon: '💰', title: '50% Savings', description: '€25-40/hr vs €50-80/hr in Athens.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical working hours.' },
      { icon: '🗣️', title: 'French & Arabic', description: 'Languages hard to find in Greece.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance standards.' },
      { icon: '🌍', title: 'MENA Access', description: 'Arabic for Middle Eastern markets.' },
      { icon: '✈️', title: 'Close Proximity', description: 'Short flights from Athens to Casablanca.' },
    ],
    advantages: [
      { title: 'EU Compliance', description: 'Same GDPR, ISO standards as Greece.' },
      { title: 'Cost Advantage', description: '50% savings vs Athenian agencies.' },
      { title: 'Language Diversity', description: 'French, Arabic, German — rare in Greece.' },
      { title: 'Full-Stack', description: 'All modern technologies in-house.' },
      { title: 'Tourism Tech', description: 'Expertise in tourism and hospitality platforms.' },
      { title: 'Maritime', description: 'Enterprise platforms for shipping companies.' },
    ],
    pricingComparison: { local: '€50-80', morocco: '€25-40', savings: '50%+' },
    industries: [
      { name: 'Tourism & Hospitality', description: 'Booking platforms and tourism apps for Greece.' },
      { name: 'Maritime & Shipping', description: 'Enterprise platforms for Greek shipping.' },
      { name: 'FinTech', description: 'Financial platforms for Greek market.' },
      { name: 'E-commerce', description: 'Online retail platforms.' },
      { name: 'Real Estate', description: 'Property platforms for international buyers.' },
      { name: 'Energy', description: 'Renewable energy management systems.' },
    ],
    faqs: [
      { question: 'How do rates compare to Athens developers?', answer: 'Senior developers in Athens cost €50-80/hr. Our rate is €25-40/hr — 50%+ savings with ISO 27001, GDPR, and broader language capabilities.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // NEW COUNTRIES: Latin America
  // ═══════════════════════════════════════════════════════════

  peru: make({
    slug: 'software-development-agency-for-peru',
    countryName: 'Peru',
    countryFlag: '🇵🇪',
    timezone: 'PET (GMT-5)',
    languages: ['Spanish (native)', 'English', 'French'],
    currency: 'PEN / USD',
    title: 'Software Development Agency Peru 2026 – Call IT Dev',
    description: 'Software development agency for Peruvian companies. EU & MENA engineering, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Peru', 'desarrollo software Perú', 'IT outsourcing Peru'],
    heroTitle: 'Software Development Agency for Peru — EU Engineering Partner',
    heroSubtitle: 'Peruvian companies going global: European engineering, Spanish-native teams, GDPR compliance. From $25/hr.',
    whyTitle: 'Why Peruvian Companies Choose Call IT Dev',
    whyIntro: 'Peru\'s tech sector expanding to EU and MENA needs multilingual engineering and GDPR compliance.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish', description: 'Seamless communication with Peruvian teams.' },
      { icon: '🌍', title: 'EU Languages', description: 'French, German, Italian for European markets.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise quality.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU data protection compliance.' },
      { icon: '⏰', title: 'Extended Hours', description: '6h offset for follow-the-sun.' },
      { icon: '🏆', title: 'Quality', description: 'CI/CD, 80%+ test coverage.' },
    ],
    advantages: [
      { title: 'EU Market Entry', description: 'Build GDPR-compliant products for Europe.' },
      { title: 'Spanish Bridge', description: 'Native Spanish + EU languages.' },
      { title: 'Mining Tech', description: 'Enterprise platforms for Peru\'s mining sector.' },
      { title: 'Full-Stack', description: 'All modern technologies.' },
      { title: 'Follow-the-Sun', description: '6h timezone complement.' },
      { title: 'Flexible', description: 'Any engagement model.' },
    ],
    pricingComparison: { local: '$15-30', morocco: '$25-40', savings: 'EU quality' },
    industries: [
      { name: 'Mining Tech', description: 'Platforms for Peru\'s mining industry.' },
      { name: 'FinTech', description: 'Financial platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail.' },
      { name: 'AgriTech', description: 'Agricultural platforms.' },
      { name: 'Tourism', description: 'Travel platforms.' },
      { name: 'SaaS', description: 'Product development.' },
    ],
    faqs: [
      { question: 'Can you work with Peruvian development teams?', answer: 'Yes. Native Spanish communication, overlapping hours, and complementary EU language skills make us an ideal extension of Peruvian engineering teams.' },
    ],
  }),

  chile: make({
    slug: 'software-development-agency-for-chile',
    countryName: 'Chile',
    countryFlag: '🇨🇱',
    timezone: 'CLT (GMT-4)',
    languages: ['Spanish (native)', 'English', 'French', 'Portuguese'],
    currency: 'CLP / USD',
    title: 'Software Development Agency Chile 2026 – Call IT Dev',
    description: 'Software development agency for Chilean companies. EU engineering, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Chile', 'desarrollo software Chile', 'IT outsourcing Chile', 'agencia desarrollo Santiago'],
    heroTitle: 'Software Development Agency for Chile — EU Digital Bridge',
    heroSubtitle: 'Chilean companies: European engineering with native Spanish. GDPR compliance, EU languages. From $25/hr.',
    whyTitle: 'Why Chilean Companies Choose Call IT Dev',
    whyIntro: 'Chile\'s innovative economy needs EU-compliant engineering for international expansion. Morocco provides native Spanish plus European languages.',
    whyReasons: [
      { icon: '🗣️', title: 'Native Spanish', description: 'Seamless communication.' },
      { icon: '🌍', title: 'EU Languages', description: 'French, German, Italian.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for EU quality.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU-grade compliance.' },
      { icon: '⏰', title: 'Extended Hours', description: '5h offset complement.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, CI/CD.' },
    ],
    advantages: [
      { title: 'EU Gateway', description: 'GDPR-ready for European markets.' },
      { title: 'Spanish Bridge', description: 'Native Spanish + EU languages.' },
      { title: 'Lithium Tech', description: 'Platforms for Chile\'s lithium/mining sector.' },
      { title: 'Full-Stack', description: 'All technologies in-house.' },
      { title: 'FinTech', description: 'Experience with LATAM fintech.' },
      { title: 'Flexible', description: 'Any engagement model.' },
    ],
    pricingComparison: { local: '$20-40', morocco: '$25-40', savings: 'EU quality' },
    industries: [
      { name: 'Mining & Lithium', description: 'Enterprise platforms for mining.' },
      { name: 'FinTech', description: 'Financial platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail.' },
      { name: 'Renewable Energy', description: 'Solar and clean energy platforms.' },
      { name: 'Wine & Agriculture', description: 'AgriTech platforms.' },
      { name: 'SaaS', description: 'Product engineering.' },
    ],
    faqs: [
      { question: 'How do Chilean and Moroccan dev rates compare?', answer: 'Rates are comparable ($25-40/hr), but Morocco adds European languages (French, German), GDPR compliance, and CET timezone coverage — capabilities rare in Chile.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // NEW COUNTRIES: Sub-Saharan Africa
  // ═══════════════════════════════════════════════════════════

  ghana: make({
    slug: 'software-development-agency-for-ghana',
    countryName: 'Ghana',
    countryFlag: '🇬🇭',
    timezone: 'GMT (GMT+0)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'GHS / USD',
    title: 'Software Development Agency Ghana 2026 – Call IT Dev',
    description: 'Software development agency for Ghanaian companies. Pan-African & EU engineering. From $25/hr.',
    keywords: ['software development agency Ghana', 'IT outsourcing Ghana', 'web development Accra'],
    heroTitle: 'Software Development Agency for Ghana — Pan-African Digital Partner',
    heroSubtitle: 'Ghanaian companies: EU languages + Arabic, GDPR-compliant, same timezone. From $25/hr.',
    whyTitle: 'Why Ghanaian Companies Choose Call IT Dev',
    whyIntro: 'Ghana\'s growing tech ecosystem needs EU-compliant engineering for international expansion.',
    whyReasons: [
      { icon: '🗣️', title: 'English & French', description: 'Cover both Anglophone and Francophone Africa.' },
      { icon: '⏰', title: 'Same Timezone', description: 'GMT+0/+1 — seamless collaboration.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for enterprise engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU-grade compliance.' },
      { icon: '🌍', title: 'Pan-African', description: 'Cover West, North, and East Africa.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, CI/CD, 80%+ test coverage.' },
    ],
    advantages: [
      { title: 'Pan-African', description: 'English + French + Arabic = continental coverage.' },
      { title: 'Same Timezone', description: 'Zero offset with Accra.' },
      { title: 'EU Ready', description: 'GDPR-compliant for European expansion.' },
      { title: 'Mobile-First', description: 'Mobile money and USSD expertise.' },
      { title: 'Full-Stack', description: 'All modern technologies.' },
      { title: 'Scale', description: 'From 2 to 50+ engineers.' },
    ],
    pricingComparison: { local: '$10-20', morocco: '$25-40', savings: 'Quality premium' },
    industries: [
      { name: 'FinTech & Mobile Money', description: 'Payment and banking platforms.' },
      { name: 'AgriTech', description: 'Digital agriculture.' },
      { name: 'EdTech', description: 'Learning platforms.' },
      { name: 'E-commerce', description: 'Online retail.' },
      { name: 'HealthTech', description: 'Telemedicine.' },
      { name: 'Mining', description: 'Enterprise platforms.' },
    ],
    faqs: [
      { question: 'Can you support Ghanaian fintech platforms?', answer: 'Yes. We have experience with mobile money, USSD, and payment integrations across Africa. We add European compliance (GDPR, PSD2) for international expansion.' },
    ],
  }),

  senegal: make({
    slug: 'software-development-agency-for-senegal',
    countryName: 'Senegal',
    countryFlag: '🇸🇳',
    timezone: 'GMT (GMT+0)',
    languages: ['French (native)', 'English', 'Arabic'],
    currency: 'XOF / EUR',
    title: 'Agence Développement Informatique Sénégal 2026 – Call IT Dev',
    description: 'Agence de développement informatique pour le Sénégal. Ingénieurs francophones natifs, RGPD. Dès 25$/h.',
    keywords: ['agence développement Sénégal', 'développement web Dakar', 'IT outsourcing Sénégal', 'software development Senegal'],
    heroTitle: 'Agence Développement Informatique pour le Sénégal — Partenaire Tech Panafricain',
    heroSubtitle: 'Entreprises sénégalaises : ingénieurs francophones natifs, RGPD-conforme, même fuseau horaire. Dès 25$/h.',
    whyTitle: 'Pourquoi les Entreprises Sénégalaises Choisissent Call IT Dev',
    whyIntro: 'Le hub numérique de Dakar a besoin d\'ingénierie EU-conforme pour l\'expansion internationale.',
    whyReasons: [
      { icon: '🗣️', title: 'Francophone Natif', description: 'Communication parfaite en français.' },
      { icon: '⏰', title: 'Même Fuseau', description: 'GMT+0/+1 — horaires identiques.' },
      { icon: '🤝', title: 'Proximité Culturelle', description: 'Liens Maroc-Sénégal historiques.' },
      { icon: '💰', title: 'Compétitif', description: '25-40$/h pour une qualité enterprise.' },
      { icon: '🛡️', title: 'RGPD', description: 'Conformité EU-grade.' },
      { icon: '🌍', title: 'Panafricain', description: 'Couverture UEMOA et CEDEAO.' },
    ],
    advantages: [
      { title: 'Hub Francophone', description: 'Couvrez toute l\'Afrique francophone.' },
      { title: 'Même Fuseau', description: 'Zéro décalage horaire.' },
      { title: 'EU Conforme', description: 'RGPD, ISO 27001.' },
      { title: 'Full-Stack', description: 'React, Node.js, Python, mobile, IA.' },
      { title: 'Arabic', description: 'Arabe natif pour le marché MENA.' },
      { title: 'Scale', description: 'De 2 à 50+ ingénieurs.' },
    ],
    pricingComparison: { local: '$8-18', morocco: '$25-40', savings: 'Quality premium' },
    industries: [
      { name: 'FinTech', description: 'Plateformes de paiement mobile.' },
      { name: 'Télécom', description: 'Plateformes opérateurs.' },
      { name: 'AgriTech', description: 'Agriculture numérique.' },
      { name: 'E-commerce', description: 'Commerce en ligne.' },
      { name: 'EdTech', description: 'Plateformes d\'apprentissage.' },
      { name: 'Énergie', description: 'Énergies renouvelables.' },
    ],
    faqs: [
      { question: 'Parlez-vous français nativement ?', answer: 'Oui. Tous nos ingénieurs sont francophones natifs. Documentation, code reviews, réunions — tout en français, comme un partenaire local.' },
    ],
  }),

  south_africa: make({
    slug: 'software-development-agency-for-south-africa',
    countryName: 'South Africa',
    countryFlag: '🇿🇦',
    timezone: 'SAST (GMT+2)',
    languages: ['English', 'French', 'Arabic', 'German'],
    currency: 'ZAR / USD',
    title: 'Software Development Agency South Africa 2026 – Call IT Dev',
    description: 'Software development agency for South African companies. EU languages, GDPR-compliant. 1h timezone offset. From $25/hr.',
    keywords: ['software development agency South Africa', 'IT outsourcing South Africa', 'web development Johannesburg', 'Cape Town development'],
    heroTitle: 'Software Development Agency for South Africa — EU & MENA Partner',
    heroSubtitle: 'South African companies: add European languages (French, German) + Arabic. 1h timezone offset, GDPR-compliant. From $25/hr.',
    whyTitle: 'Why South African Companies Choose Call IT Dev',
    whyIntro: 'South Africa\'s tech sector is strong in English — Morocco adds French, Arabic, German, and EU compliance for international expansion.',
    whyReasons: [
      { icon: '🗣️', title: 'EU Languages', description: 'French, German, Spanish — rare in SA.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical working hours.' },
      { icon: '🌍', title: 'MENA Access', description: 'Native Arabic for Middle Eastern markets.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr.' },
      { icon: '🛡️', title: 'GDPR & POPIA', description: 'Both EU and SA compliance.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, CI/CD, code reviews.' },
    ],
    advantages: [
      { title: 'Language Complement', description: 'Add French, Arabic, German to SA\'s English capability.' },
      { title: 'Pan-African', description: 'Cover Francophone Africa together.' },
      { title: 'EU Gateway', description: 'GDPR-ready for European expansion.' },
      { title: 'Close Timezone', description: '1h — seamless collaboration.' },
      { title: 'Full-Stack', description: 'All technologies.' },
      { title: 'Flexible', description: 'Any engagement model.' },
    ],
    pricingComparison: { local: '$20-45', morocco: '$25-40', savings: 'Language premium' },
    industries: [
      { name: 'Mining Tech', description: 'Enterprise platforms for mining.' },
      { name: 'FinTech', description: 'Financial platforms.' },
      { name: 'E-commerce', description: 'Cross-border retail.' },
      { name: 'InsurTech', description: 'Insurance platforms.' },
      { name: 'Tourism', description: 'Travel platforms.' },
      { name: 'AgriTech', description: 'Agricultural technology.' },
    ],
    faqs: [
      { question: 'How do you complement SA developers?', answer: 'We add French, Arabic, and German capabilities that are extremely rare in South Africa, plus GDPR compliance for EU markets. Only 1h timezone difference for seamless collaboration.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // NEW COUNTRIES: Southeast Asia
  // ═══════════════════════════════════════════════════════════

  indonesia: make({
    slug: 'software-development-agency-for-indonesia',
    countryName: 'Indonesia',
    countryFlag: '🇮🇩',
    timezone: 'WIB (GMT+7)',
    languages: ['English', 'French', 'Arabic'],
    currency: 'IDR / USD',
    title: 'Software Development Agency Indonesia 2026 – Call IT Dev',
    description: 'Software development agency for Indonesian companies. EU & MENA engineering, GDPR-compliant. From $25/hr.',
    keywords: ['software development agency Indonesia', 'IT outsourcing Indonesia', 'web development Jakarta'],
    heroTitle: 'Software Development Agency for Indonesia — EU & MENA Bridge',
    heroSubtitle: 'Indonesian companies going global: European languages, Arabic, GDPR compliance. Follow-the-sun. From $25/hr.',
    whyTitle: 'Why Indonesian Companies Choose Call IT Dev',
    whyIntro: 'Indonesia\'s 280M market going international needs EU-compliant, multilingual engineering.',
    whyReasons: [
      { icon: '🌍', title: 'EU Languages', description: 'French, German, Spanish for European markets.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: '6h offset extends development hours.' },
      { icon: '💰', title: 'Competitive', description: '$25-40/hr for EU-grade engineering.' },
      { icon: '🛡️', title: 'GDPR', description: 'EU data protection compliance.' },
      { icon: '🗣️', title: 'Arabic', description: 'Native Arabic for MENA expansion.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, CI/CD.' },
    ],
    advantages: [
      { title: 'EU Expansion', description: 'GDPR-ready products for European markets.' },
      { title: 'MENA Bridge', description: 'Arabic capability for Middle Eastern markets.' },
      { title: 'Follow-the-Sun', description: '6h complement for 24/7 development.' },
      { title: 'Full-Stack', description: 'All modern technologies.' },
      { title: 'E-commerce', description: 'Cross-border retail expertise.' },
      { title: 'Flexible', description: 'Any engagement model.' },
    ],
    pricingComparison: { local: '$8-20', morocco: '$25-40', savings: 'EU premium' },
    industries: [
      { name: 'E-commerce', description: 'Marketplace platforms for global expansion.' },
      { name: 'FinTech', description: 'Payment and banking platforms.' },
      { name: 'Ride-hailing', description: 'Mobility platforms.' },
      { name: 'EdTech', description: 'Learning platforms.' },
      { name: 'HealthTech', description: 'Telemedicine.' },
      { name: 'SaaS', description: 'Product development.' },
    ],
    faqs: [
      { question: 'How does Morocco complement Indonesian developers?', answer: 'We add European languages, GDPR compliance, and CET timezone coverage. This enables Indonesian companies to serve EU and MENA markets with native-quality interfaces.' },
    ],
  }),

  singapore: make({
    slug: 'software-development-agency-for-singapore',
    countryName: 'Singapore',
    countryFlag: '🇸🇬',
    timezone: 'SGT (GMT+8)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'SGD / USD',
    title: 'Software Development Agency Singapore 2026 – Call IT Dev',
    description: 'Software development agency for Singaporean companies. EU languages, GDPR-compliant. 50% savings. From $25/hr.',
    keywords: ['software development agency Singapore', 'IT outsourcing Singapore', 'web development Singapore'],
    heroTitle: 'Software Development Agency for Singapore — EU Language Partner',
    heroSubtitle: 'Singaporean companies: add French, German, Arabic engineering. 50% savings, GDPR-compliant. Follow-the-sun.',
    whyTitle: 'Why Singaporean Companies Choose Call IT Dev',
    whyIntro: 'Singapore\'s global companies need European and MENA language engineering at scale.',
    whyReasons: [
      { icon: '🗣️', title: 'EU Languages', description: 'French, German, Spanish, Italian — rare in Singapore.' },
      { icon: '💰', title: '50% Savings', description: 'vs Singapore-based multilingual development.' },
      { icon: '🛡️', title: 'GDPR & PDPA', description: 'Both EU and Singapore compliance.' },
      { icon: '⏰', title: 'Follow-the-Sun', description: '7h offset for extended EU coverage.' },
      { icon: '🌍', title: 'MENA Access', description: 'Arabic for Middle Eastern markets.' },
      { icon: '🏆', title: 'Enterprise', description: 'ISO 27001, SOC 2-aligned.' },
    ],
    advantages: [
      { title: 'EU Market Access', description: 'European language UX for Singapore\'s EU operations.' },
      { title: 'Cost Efficiency', description: '50% savings on multilingual development.' },
      { title: 'MENA Bridge', description: 'Arabic and French for MENA markets.' },
      { title: 'Enterprise Security', description: 'ISO 27001, MAS-compatible.' },
      { title: 'Follow-the-Sun', description: 'Cover EU hours while Singapore sleeps.' },
      { title: 'Full-Stack', description: 'All technologies in-house.' },
    ],
    pricingComparison: { local: '$50-100 (SGD)', morocco: '$25-40', savings: '50%+' },
    industries: [
      { name: 'FinTech', description: 'Financial platforms for Singapore\'s fintech hub.' },
      { name: 'Maritime & Logistics', description: 'Shipping and logistics platforms.' },
      { name: 'SaaS', description: 'Product development and scaling.' },
      { name: 'E-commerce', description: 'Cross-border retail.' },
      { name: 'Gaming', description: 'Game platforms.' },
      { name: 'Consulting', description: 'Internal tools for consulting firms.' },
    ],
    faqs: [
      { question: 'How do Singaporean and Moroccan rates compare?', answer: 'Senior developers in Singapore cost $50-100/hr. Our rate is $25-40/hr — 50%+ savings with European language capabilities and GDPR compliance that are difficult to source in Singapore.' },
    ],
  }),

  // ═══════════════════════════════════════════════════════════
  // NEW COUNTRIES: Eastern Europe
  // ═══════════════════════════════════════════════════════════

  romania: make({
    slug: 'software-development-agency-for-romania',
    countryName: 'Romania',
    countryFlag: '🇷🇴',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'RON / EUR',
    title: 'Software Development Agency Romania 2026 – Call IT Dev',
    description: 'Software development agency for Romanian companies. French, Arabic capability, GDPR. 40% savings vs Bucharest. From €25/hr.',
    keywords: ['software development agency Romania', 'IT outsourcing Romania', 'web development Bucharest'],
    heroTitle: 'Software Development Agency for Romania — Nearshore Alternative',
    heroSubtitle: 'Romanian companies: add French, Arabic, German engineering. 40% savings vs Bucharest. 1h timezone offset.',
    whyTitle: 'Why Romanian Companies Choose Call IT Dev',
    whyIntro: 'Romania\'s booming IT sector faces talent shortages. Morocco adds language diversity and cost advantage.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Languages scarce in Romania.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical working hours.' },
      { icon: '💰', title: '40% Savings', description: 'vs Bucharest development costs.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance.' },
      { icon: '🌍', title: 'MENA Access', description: 'Arabic for Middle Eastern markets.' },
      { icon: '🚀', title: 'Fast Setup', description: '2-week team assembly.' },
    ],
    advantages: [
      { title: 'Language Diversity', description: 'French, Arabic, German complement Romanian English.' },
      { title: 'Cost Advantage', description: '40% savings vs Bucharest.' },
      { title: 'MENA Markets', description: 'Arabic capability for regional expansion.' },
      { title: 'EU Standards', description: 'Same GDPR, ISO compliance.' },
      { title: 'Full-Stack', description: 'All modern technologies.' },
      { title: 'Close Timezone', description: '1h difference.' },
    ],
    pricingComparison: { local: '€25-50', morocco: '€25-40', savings: '30-40%' },
    industries: [
      { name: 'IT & Software', description: 'Product engineering.' },
      { name: 'Automotive', description: 'Enterprise platforms.' },
      { name: 'Banking', description: 'Financial applications.' },
      { name: 'E-commerce', description: 'Online retail.' },
      { name: 'Telecom', description: 'Operator platforms.' },
      { name: 'BPO', description: 'White-label development for Romanian outsourcers.' },
    ],
    faqs: [
      { question: 'How do Moroccan and Romanian developers compare?', answer: 'Both countries have strong engineering talent. Morocco adds French, Arabic, and German capabilities that are very hard to find in Romania, at similar or lower rates.' },
    ],
  }),

  bulgaria: make({
    slug: 'software-development-agency-for-bulgaria',
    countryName: 'Bulgaria',
    countryFlag: '🇧🇬',
    timezone: 'EET (GMT+2)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'BGN / EUR',
    title: 'Software Development Agency Bulgaria 2026 – Call IT Dev',
    description: 'Software development agency for Bulgarian companies. French, Arabic, German capability. From €25/hr.',
    keywords: ['software development agency Bulgaria', 'IT outsourcing Bulgaria', 'web development Sofia'],
    heroTitle: 'Software Development Agency for Bulgaria — EU Nearshore from Morocco',
    heroSubtitle: 'Bulgarian companies: French, Arabic, German engineering. 1h timezone offset, GDPR-compliant. From €25/hr.',
    whyTitle: 'Why Bulgarian Companies Choose Call IT Dev',
    whyIntro: 'Bulgaria\'s cost-effective IT scene benefits from Morocco\'s multilingual capabilities.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Not available in Bulgarian talent pool.' },
      { icon: '⏰', title: '1h Offset', description: 'Near-identical hours.' },
      { icon: '💰', title: 'Competitive', description: 'Similar rates, broader language coverage.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance.' },
      { icon: '🌍', title: 'MENA', description: 'Arabic for regional markets.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, enterprise-grade.' },
    ],
    advantages: [
      { title: 'Language Expansion', description: 'French, Arabic, German — rare in Bulgaria.' },
      { title: 'EU Compliance', description: 'Same GDPR standards.' },
      { title: 'Competitive', description: 'Similar costs with broader capabilities.' },
      { title: 'MENA Bridge', description: 'Arabic engineering.' },
      { title: 'Full-Stack', description: 'All technologies.' },
      { title: 'Timezone', description: '1h difference.' },
    ],
    pricingComparison: { local: '€20-40', morocco: '€25-40', savings: 'Language premium' },
    industries: [
      { name: 'IT Outsourcing', description: 'White-label for Bulgarian outsourcers.' },
      { name: 'Tourism', description: 'Travel platforms.' },
      { name: 'E-commerce', description: 'Online retail.' },
      { name: 'Manufacturing', description: 'Enterprise platforms.' },
      { name: 'Real Estate', description: 'Property platforms.' },
      { name: 'Energy', description: 'Energy management.' },
    ],
    faqs: [
      { question: 'Why choose Morocco over Bulgaria?', answer: 'We complement Bulgarian IT by adding French, Arabic, and Spanish capabilities that are almost impossible to find in Bulgaria. Similar rates, broader international coverage.' },
    ],
  }),

  czech_republic: make({
    slug: 'software-development-agency-for-czech-republic',
    countryName: 'Czech Republic',
    countryFlag: '🇨🇿',
    timezone: 'CET (GMT+1)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'CZK / EUR',
    title: 'Software Development Agency Czech Republic 2026 – Call IT Dev',
    description: 'Software development agency for Czech companies. French, Arabic capability, same CET timezone. 45% savings vs Prague.',
    keywords: ['software development agency Czech Republic', 'IT outsourcing Czechia', 'web development Prague'],
    heroTitle: 'Software Development Agency for Czech Republic — Same CET, Better Value',
    heroSubtitle: 'Czech companies: French, Arabic, German engineering in same CET timezone. 45% savings vs Prague. ISO 27001.',
    whyTitle: 'Why Czech Companies Choose Call IT Dev',
    whyIntro: 'Prague\'s developer shortage and rising costs make Morocco an attractive nearshore alternative with broader language capabilities.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Nearly impossible to recruit in Czechia.' },
      { icon: '⏰', title: 'Same CET', description: 'Identical timezone — zero friction.' },
      { icon: '💰', title: '45% Savings', description: 'vs Prague developer costs.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance.' },
      { icon: '🌍', title: 'MENA Markets', description: 'Arabic for Middle Eastern expansion.' },
      { icon: '🚀', title: 'Fast', description: '2-week team assembly.' },
    ],
    advantages: [
      { title: 'CET Alignment', description: 'Same timezone — no scheduling issues.' },
      { title: 'Cost Advantage', description: '45% savings vs Prague.' },
      { title: 'Language Diversity', description: 'French, Arabic, Spanish.' },
      { title: 'Automotive', description: 'Enterprise platforms for Czech auto sector.' },
      { title: 'Full-Stack', description: 'All technologies.' },
      { title: 'EU Standards', description: 'ISO 27001, GDPR.' },
    ],
    pricingComparison: { local: '€35-65', morocco: '€25-40', savings: '45%+' },
    industries: [
      { name: 'Automotive', description: 'Platforms for Czech auto industry.' },
      { name: 'Manufacturing', description: 'Enterprise systems.' },
      { name: 'IT & SaaS', description: 'Product engineering.' },
      { name: 'Tourism', description: 'Travel platforms.' },
      { name: 'E-commerce', description: 'Online retail.' },
      { name: 'Energy', description: 'Utility platforms.' },
    ],
    faqs: [
      { question: 'How do costs compare to Prague?', answer: 'Senior developers in Prague cost €35-65/hr. Our rate is €25-40/hr — 45% savings with additional French, Arabic, and Spanish capabilities.' },
    ],
  }),

  hungary: make({
    slug: 'software-development-agency-for-hungary',
    countryName: 'Hungary',
    countryFlag: '🇭🇺',
    timezone: 'CET (GMT+1)',
    languages: ['English', 'French', 'German', 'Arabic'],
    currency: 'HUF / EUR',
    title: 'Software Development Agency Hungary 2026 – Call IT Dev',
    description: 'Software development agency for Hungarian companies. French, Arabic capability, same CET. 40% savings vs Budapest.',
    keywords: ['software development agency Hungary', 'IT outsourcing Hungary', 'web development Budapest'],
    heroTitle: 'Software Development Agency for Hungary — CET Nearshore from Morocco',
    heroSubtitle: 'Hungarian companies: French, Arabic, German engineering. Same CET timezone, 40% savings vs Budapest.',
    whyTitle: 'Why Hungarian Companies Choose Call IT Dev',
    whyIntro: 'Budapest\'s shared services sector benefits from Morocco\'s multilingual engineering at lower costs.',
    whyReasons: [
      { icon: '🗣️', title: 'French & Arabic', description: 'Languages hard to recruit in Hungary.' },
      { icon: '⏰', title: 'Same CET', description: 'Identical timezone.' },
      { icon: '💰', title: '40% Savings', description: 'vs Budapest costs.' },
      { icon: '🛡️', title: 'GDPR', description: 'Same EU compliance.' },
      { icon: '🌍', title: 'MENA', description: 'Arabic for regional markets.' },
      { icon: '🏆', title: 'Quality', description: 'ISO 27001, enterprise-grade.' },
    ],
    advantages: [
      { title: 'SSC Alternative', description: 'More cost-effective than Budapest SSCs.' },
      { title: 'Language Diversity', description: 'French, Arabic, Spanish.' },
      { title: 'Same Timezone', description: 'CET alignment.' },
      { title: 'Automotive', description: 'Platforms for Hungarian auto sector.' },
      { title: 'Full-Stack', description: 'All technologies.' },
      { title: 'EU Standards', description: 'GDPR, ISO 27001.' },
    ],
    pricingComparison: { local: '€30-55', morocco: '€25-40', savings: '40%+' },
    industries: [
      { name: 'Automotive', description: 'Enterprise platforms for auto manufacturers.' },
      { name: 'Shared Services', description: 'Development for SSC operations.' },
      { name: 'Pharma', description: 'Healthcare platforms.' },
      { name: 'IT & Telecom', description: 'Tech platforms.' },
      { name: 'E-commerce', description: 'Online retail.' },
      { name: 'Banking', description: 'Financial applications.' },
    ],
    faqs: [
      { question: 'How do you compare to Budapest SSCs?', answer: 'We offer 40% savings with additional French, Arabic, and Spanish — languages that Budapest SSCs struggle to recruit. Same CET timezone, same EU compliance.' },
    ],
  }),
};
